// Reglas puras de cobro del AccountEntry (sin dependencias de Vue/store), para
// poder testearlas de forma exhaustiva. El componente delega aquí la decisión
// de "¿este cobro es válido y qué se registra?".
//
// Contrato del backend (batch): por cada movimiento se registra
//   - `amount`: monto APLICADO al documento (== total de la venta/compra)
//   - `tip`:    propina, en su propia columna (solo ventas)
// y a la cuenta entra físicamente `amount + tip`. El batch valida que
// `Σ amount == total`. Por eso la propina NUNCA forma parte de `amount`.

export const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

/**
 * Resuelve un cobro de MODO SIMPLE (un solo movimiento que cubre todo el total).
 *
 * @param {object} p
 * @param {number} p.total        Total del documento (venta) o neto a pagar (compra con retención).
 * @param {string} p.paymentCode  'CASH' | 'TRANSFER' | 'CHECK' | 'DEBIT_CARD' | 'CREDIT_CARD'.
 * @param {number} p.inputAmount  Valor recibido (CASH) o valor cargado (no-CASH).
 * @param {number} [p.tip]        Propina ingresada (ignorada en compras).
 * @param {boolean} [p.isPurchase]
 * @returns {{ok:boolean, error:(string|null), amount:number, tip:number, change:number, totalMoved:number, excess?:number}}
 */
export function resolveSimpleEntry({ total, paymentCode, inputAmount, tip = 0, isPurchase = false }) {
    const saleTotal = round2(total);
    const tipEff = isPurchase ? 0 : round2(tip || 0);
    const received = round2(inputAmount || 0);
    const totalWithTip = round2(saleTotal + tipEff);
    const base = { amount: saleTotal, tip: tipEff, change: 0, totalMoved: totalWithTip };

    if (received <= 0) {
        return { ...base, ok: false, error: 'amount-required' };
    }

    if (paymentCode === 'CASH') {
        // Efectivo: se puede dar cambio. El valor recibido debe cubrir
        // total + propina; el excedente es cambio a devolver.
        if (received < totalWithTip) {
            return { ...base, ok: false, error: 'cash-insufficient', change: round2(received - totalWithTip) };
        }
        return { ...base, ok: true, error: null, change: round2(received - totalWithTip) };
    }

    // No-CASH (transferencia, cheque, tarjeta): NO se puede dar cambio. El valor
    // cargado debe ser exactamente total + propina. Cualquier excedente sobre el
    // total del documento tiene que registrarse como propina (o bloquea).
    const diff = round2(received - totalWithTip);
    if (diff > 0) {
        return { ...base, ok: false, error: 'excess-must-be-tip', excess: round2(received - saleTotal) };
    }
    if (diff < 0) {
        return { ...base, ok: false, error: 'amount-mismatch' };
    }
    return { ...base, ok: true, error: null };
}

/**
 * Resuelve un pago de MODO MÚLTIPLE (una porción de un pago MIXTO).
 *
 * El `amount` aplicado nunca puede exceder lo que resta del total; la propina es
 * aditiva y vive en su propio campo (jamás infla el monto aplicado ni el
 * restante). Así, ingresar propina no dispara la alerta de "excede el restante".
 *
 * @returns {{ok:boolean, error:(string|null), amount:number, tip:number}}
 */
export function resolveMultiplePayment({ remaining, inputAmount, tip = 0, isPurchase = false }) {
    const rem = round2(remaining);
    const applied = round2(inputAmount || 0);
    const tipEff = isPurchase ? 0 : round2(tip || 0);

    if (applied <= 0) {
        return { ok: false, error: 'amount-required', amount: applied, tip: tipEff };
    }
    // Tolerancia de 1 centavo para absorber ruido de coma flotante.
    if (applied - rem > 0.001) {
        return { ok: false, error: 'exceeds-remaining', amount: applied, tip: tipEff };
    }
    return { ok: true, error: null, amount: applied, tip: tipEff };
}

/**
 * Resuelve un ABONO (MODO PARTIAL): un pago parcial contra el saldo pendiente
 * de una cuenta por cobrar (venta) o por pagar (compra).
 *
 * Contrato del backend (`registerPayment`): `0 < amount <= pendiente`. Los
 * abonos NUNCA llevan propina ni dan cambio — el monto ingresado es exactamente
 * lo que se aplica al documento y lo que entra/sale de la cuenta.
 *
 * `completes` indica si este abono deja el saldo en cero (el backend marcará
 * el documento como PAID).
 *
 * @param {object} p
 * @param {number} p.pending      Saldo pendiente del documento (total − abonado).
 * @param {number} p.inputAmount  Monto del abono.
 * @returns {{ok:boolean, error:(string|null), amount:number, completes:boolean}}
 */
export function resolvePartialEntry({ pending, inputAmount }) {
    const rem = round2(pending);
    const applied = round2(inputAmount || 0);

    if (rem <= 0) {
        return { ok: false, error: 'nothing-pending', amount: applied, completes: false };
    }
    if (applied <= 0) {
        return { ok: false, error: 'amount-required', amount: applied, completes: false };
    }
    // Tolerancia de 1 centavo para absorber ruido de coma flotante.
    if (applied - rem > 0.001) {
        return { ok: false, error: 'exceeds-pending', amount: applied, completes: false };
    }
    return { ok: true, error: null, amount: applied, completes: Math.abs(applied - rem) <= 0.001 };
}

const MONEY_TYPE_NAMES = { CASH: 'efectivo', CARD: 'tarjeta', TRANSFER: 'transferencia' };

/**
 * Aviso del diálogo de cobro cuando no hay cuenta que elegir (H36), o `null` si la hay.
 *
 * El store tiene las cuentas de la actividad activa; el diálogo las filtra por tipo de dinero.
 * Sin ninguna, el cobro no puede guardarse: antes se elegía una cuenta de la actividad
 * anterior que el store conservaba, el backend la rechazaba y el cobro se perdía.
 *
 * @param {Array} allAccounts  Cuentas de la actividad activa (todas).
 * @param {(string|null)} moneyType  'CASH' | 'CARD' | 'TRANSFER', o `null` si aún no hay método
 *                                   (pago mixto): entonces solo avisa si no hay ninguna cuenta.
 * @returns {(string|null)}
 */
export function noAccountsMessage(allAccounts, moneyType) {
    const accounts = allAccounts || [];
    if (accounts.length === 0) {
        return 'Esta actividad no tiene cuentas: crea una en Cuentas.';
    }
    if (moneyType && !accounts.some((a) => a.moneyType === moneyType)) {
        return `Esta actividad no tiene cuentas de ${MONEY_TYPE_NAMES[moneyType] || 'este tipo'}: crea una en Cuentas.`;
    }
    return null;
}

/**
 * Detalle del aviso cuando el backend rechaza un cobro o pago (H36): su motivo, si lo dio,
 * antes de la indicación de reintento. Solo los 422 traen un motivo para el usuario; el resto
 * (red, 500) trae textos técnicos que no se muestran.
 *
 * @param {object} response  Respuesta de `createMultipleTransactions` (`{status, error}`).
 * @param {string} fallback  Texto de siempre ("Use el botón … para reintentar.").
 * @returns {string}
 */
export function rejectionDetail(response, fallback) {
    if (!response || response.status !== 422) return fallback;
    const reason = typeof response.error === 'string' ? response.error.trim() : '';
    return reason ? `${reason} ${fallback}` : fallback;
}

/**
 * App-status 910 (`alreadyCollected`): el cobro o el pago YA estaba registrado —doble clic,
 * otra pestaña, o un reintento después de que la respuesta se perdió—. Reintentar nunca va a
 * funcionar, así que no se invita a hacerlo: se refresca el documento y se avisa.
 */
export const ALREADY_COLLECTED = 910;

export function isAlreadyCollected(response) {
    return !!response && response.status === ALREADY_COLLECTED;
}

/**
 * Aviso del 910: el texto del backend («Esta venta ya fue cobrada», «Esta compra ya fue
 * pagada») o `fallback`, sin indicación de reintento.
 */
export function alreadyCollectedToast(response, fallback) {
    const reason = response && typeof response.error === 'string' ? response.error.trim() : '';
    return { severity: 'info', summary: 'Ya estaba registrado', detail: reason || fallback, life: 5000 };
}
